import { createSelector } from '@reduxjs/toolkit'
import { selectApp } from 'selectors/app'
import { selectThemeId } from 'selectors/landingPageTheme'
import { selectOrderingEnabled } from 'selectors/merchant'
import { api } from './index'

// Define PlacementType in the API layer to avoid code smell
export type PlacementType = 'top' | 'bottom'

export type NavigationTabsSettings = {
  navigation_tab_bg_color?: string
  navigation_tab_label_selected_color?: string
  navigation_tab_label_default_color?: string
  navigation_placement?: PlacementType
}

export type NavigationStyleUpdatePayload = {
  appId: number
  themeId: number
  navigation_tabs: NavigationTabsSettings
}

export type NavigationStyleResponse = {
  id: number
  settings: {
    navigation_tab_bg_color: string | null
    navigation_tab_label_selected_color: string | null
    navigation_tab_label_default_color: string | null
    navigation_placement: PlacementType | null
  }
}

export const navigationStyleApi = api.injectEndpoints({
  endpoints: build => ({
    getNavigationStyle: build.query<
      NavigationStyleResponse,
      { appId: number; themeId: number }
    >({
      query: ({ appId, themeId }) => ({
        url: `apps/${appId}/landing_page_themes/${themeId}/navigation_settings`,
        method: 'GET',
      }),
      providesTags: ['LandingPageTheme'],
    }),
    updateNavigationStyle: build.mutation<
      NavigationStyleResponse,
      NavigationStyleUpdatePayload
    >({
      query: payload => ({
        url: `apps/${payload.appId}/landing_page_themes/${payload.themeId}/navigation_settings`,
        method: 'PUT',
        body: {
          navigation_settings: payload.navigation_tabs,
        },
      }),
      invalidatesTags: ['LandingPageTheme'],
    }),
  }),
})

// Export hooks
export const { useGetNavigationStyleQuery, useUpdateNavigationStyleMutation } =
  navigationStyleApi

// Selectors for loading states using createSelector
export const selectNavigationStyleIsLoading = createSelector(
  [selectApp, selectThemeId, state => state],
  (app, themeId, state) => {
    if (!app?.id || !themeId) return false
    const queryState = navigationStyleApi.endpoints.getNavigationStyle.select({
      appId: app.id,
      themeId,
    })(state)
    return (queryState as any).isFetching || false
  }
)

export const selectNavigationStyleIsSubmitting = createSelector(
  [state => state.api?.mutations || {}],
  mutations => {
    return Object.values(mutations).some(
      (mutation: any) =>
        mutation?.endpointName === 'updateNavigationStyle' &&
        mutation?.status === 'pending'
    )
  }
)

export const selectNavigationStyleApiData = createSelector(
  [selectApp, selectThemeId, selectOrderingEnabled, state => state],
  (app, themeId, orderingEnabled, state) => {
    if (!app?.id || !themeId) return undefined

    const queryState = navigationStyleApi.endpoints.getNavigationStyle.select({
      appId: app.id,
      themeId,
    })(state)

    const data = (queryState as any)?.data
    return data
  }
)
